'use client';

import { useEffect } from 'react';

interface GlobalErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

/**
 * Root-level error boundary — catches render-time exceptions that escape
 * every route-level error.tsx. Without this, any uncaught render error
 * produces a blank white screen with no recovery path.
 */
export default function GlobalError({ error, reset }: GlobalErrorProps) {
  useEffect(() => {
    console.error('[GlobalError] Uncaught render error:', {
      message: error.message,
      stack: error.stack,
      digest: error.digest,
      timestamp: new Date().toISOString(),
    });
  }, [error]);

  return (
    <html>
      <body style={{ margin: 0, background: '#0a0a0b', color: '#e0e0e0', fontFamily: 'system-ui, sans-serif' }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '2rem',
          textAlign: 'center',
        }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 600, margin: '0 0 0.5rem' }}>
            Something went wrong
          </h1>
          <p style={{ color: '#888', margin: '0 0 2rem', maxWidth: '32rem' }}>
            The application encountered an unexpected error. Your data is safe — this is
            a display issue that a refresh will fix.
          </p>
          <button
            onClick={reset}
            style={{
              padding: '0.625rem 1.5rem',
              fontSize: '0.875rem',
              fontWeight: 500,
              background: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '0.375rem',
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
          <p style={{ color: '#555', fontSize: '0.75rem', marginTop: '2rem' }}>
            If this persists, please contact support.
          </p>
          {process.env.NODE_ENV === 'development' && (
            <pre style={{
              marginTop: '1.5rem',
              padding: '1rem',
              background: '#1a1a1a',
              borderRadius: '0.375rem',
              fontSize: '0.75rem',
              textAlign: 'left',
              maxWidth: '100%',
              overflow: 'auto',
              color: '#ef4444',
            }}>
              {error.message}
              {'\n\n'}
              {error.stack}
            </pre>
          )}
        </div>
      </body>
    </html>
  );
}
